import {
  ECONOMY,
  VENUES,
  type GameId,
  type LedgerKind,
  type LedgerLine,
  type WalletResponse,
} from '@tira/protocol';
import { useCallback, useEffect, useState, type JSX } from 'react';
import { Button } from '../components/Button.js';
import { useT, type CopyKey } from '../i18n/index.js';
import { api } from '../net/api.js';
import { useApp } from '../state/store.js';
import { chips, signedChips, whenShort } from './lib/format.js';
import { Screen } from './parts/Screen.js';
import './Wallet.css';

/**
 * S14 — pitaka. Balance, where every chip came from, the streak ladder and the
 * two faucets a player can pull themselves (baon, rescue).
 */
export function Wallet(): JSX.Element {
  const me = useApp((s) => s.me);
  const balance = useApp((s) => s.balance);
  const refreshMe = useApp((s) => s.refreshMe);
  const setToast = useApp((s) => s.setToast);
  const go = useApp((s) => s.go);
  const t = useT();

  const [wallet, setWallet] = useState<WalletResponse | null>(null);
  const [failed, setFailed] = useState(false);
  const [busy, setBusy] = useState<'baon' | 'rescue' | null>(null);

  const load = useCallback(() => {
    api
      .wallet()
      .then((w) => {
        setWallet(w);
        setFailed(false);
      })
      .catch(() => setFailed(true));
  }, []);

  useEffect(load, [load]);

  const more = () => {
    const cursor = wallet?.nextCursor;
    if (!cursor) return;
    api
      .wallet(cursor)
      .then((w) =>
        setWallet((prev) => (prev ? { ...w, lines: [...prev.lines, ...w.lines] } : w)),
      )
      .catch(() => setToast(t('account.wallet.page.fail'), 'warn'));
  };

  const claimBaon = () => {
    setBusy('baon');
    api
      .claimBaon()
      .then((r) => {
        setBusy(null);
        setToast(
          t('account.wallet.baon.got', { n: chips(r.credited), days: r.streak }),
          'win',
        );
        void refreshMe();
        load();
      })
      .catch((e: Error) => {
        setBusy(null);
        setToast(t('account.wallet.baon.fail', { err: e.message }), 'warn');
      });
  };

  const claimRescue = () => {
    setBusy('rescue');
    api
      .claimRescue()
      .then((r) => {
        setBusy(null);
        setToast(t('account.wallet.rescue.got', { n: chips(r.credited) }), 'win');
        void refreshMe();
        load();
      })
      .catch((e: Error) => {
        setBusy(null);
        setToast(t('account.wallet.rescue.fail', { err: e.message }), 'warn');
      });
  };

  const streakDays = me?.streak.days ?? 0;
  const claimedToday = me?.streak.claimedToday ?? false;
  const canRescue = balance < ECONOMY.rescueThreshold;

  return (
    <Screen title={t('account.wallet.title')} sub={t('account.wallet.sub')}>
      <section className="s-panel">
        <div className="wallet__balance">
          <p className="wallet__big">
            {chips(balance)}
            <small>{t('account.wallet.held')}</small>
          </p>
          {wallet && wallet.escrowed > 0 ? (
            <p className="s-note">
              {t('account.wallet.escrow')} <b className="s-row__data">{chips(wallet.escrowed)}</b>.{' '}
              {t('account.wallet.escrow.back')}
            </p>
          ) : null}
        </div>

        <div className="wallet__claims">
          <Button variant="primary" disabled={claimedToday || busy !== null} onClick={claimBaon}>
            {claimedToday ? t('account.wallet.baon.done') : t('account.wallet.baon.claim')}
          </Button>
          <Button variant="secondary" disabled={!canRescue || busy !== null} onClick={claimRescue}>
            {t('account.wallet.rescue.claim', { n: chips(ECONOMY.rescueAmount) })}
          </Button>
          <span className="s-note">
            {canRescue
              ? t('account.wallet.rescue.note', {
                  cap: ECONOMY.rescueDailyCap,
                  n: chips(ECONOMY.rescueThreshold),
                })
              : t('account.wallet.rescue.locked', { n: chips(ECONOMY.rescueThreshold) })}
          </span>
        </div>
      </section>

      <section className="s-panel">
        <div className="s-panel__head">
          <h2>{t('account.wallet.streak.title')}</h2>
          <p>{t('account.wallet.streak.sub')}</p>
        </div>
        <div className="streak">
          {ECONOMY.baonLadder.map((amount, i) => (
            <div
              key={i}
              className="streak__day"
              data-done={i < streakDays}
              data-today={i === Math.min(streakDays, ECONOMY.baonLadder.length - 1)}
            >
              <span>{t('account.wallet.day', { n: i + 1 })}</span>
              <b>{chips(amount)}</b>
            </div>
          ))}
        </div>
        <p className="s-note">
          {claimedToday
            ? t('account.wallet.streak.done', {
                n: chips(me?.streak.nextAmount ?? ECONOMY.baonBase),
              })
            : t('account.wallet.streak.waiting', {
                n: chips(me?.streak.nextAmount ?? ECONOMY.baonBase),
              })}
        </p>
      </section>

      <section className="s-panel">
        <div className="s-panel__head">
          <h2>{t('account.wallet.stats.title')}</h2>
          <p>{t('account.wallet.stats.sub')}</p>
        </div>
        <div className="wallet__stats">
          {(Object.keys(VENUES) as GameId[]).map((g) => {
            const s = me?.stats.byGame[g];
            const played = s?.played ?? 0;
            const won = s?.won ?? 0;
            return (
              <div className="wallet__stat" key={g}>
                <b>{t(`venue.${g}`)}</b>
                <span>
                  {played === 0
                    ? t('account.wallet.stats.none')
                    : t('account.wallet.stats.record', { won, lost: played - won, played })}
                </span>
              </div>
            );
          })}
        </div>
      </section>

      <section className="s-panel">
        <div className="s-panel__head">
          <h2>{t('account.wallet.ledger.title')}</h2>
          <p>{t('account.wallet.ledger.sub')}</p>
        </div>

        {failed ? (
          <div className="s-empty">
            <b>{t('account.wallet.ledger.failed')}</b>
            <span>{t('account.wallet.ledger.failed.sub')}</span>
            <Button variant="secondary" onClick={load}>
              {t('account.try')}
            </Button>
          </div>
        ) : !wallet ? (
          <p className="s-note">{t('account.wallet.ledger.loading')}</p>
        ) : wallet.lines.length === 0 ? (
          <div className="s-empty">
            <b>{t('account.wallet.ledger.empty')}</b>
            <span>{t('account.wallet.ledger.empty.sub')}</span>
            <Button variant="secondary" onClick={() => go({ s: 'compound' })}>
              {t('account.wallet.ledger.back')}
            </Button>
          </div>
        ) : (
          <>
            <div className="ledger">
              {wallet.lines.map((line) => (
                <LedgerRow key={line.id} line={line} />
              ))}
            </div>
            {wallet.nextCursor ? (
              <Button variant="ghost" onClick={more}>
                {t('account.wallet.more')}
              </Button>
            ) : null}
          </>
        )}
      </section>
    </Screen>
  );
}

function LedgerRow({ line }: { line: LedgerLine }): JSX.Element {
  const t = useT();
  return (
    <div className="ledger__row">
      <div className="ledger__what">
        <b>{t(KIND_LABEL[line.kind])}</b>
        <span>
          {whenShort(line.at)}
          {line.memo ? ` · ${line.memo}` : ''}
        </span>
      </div>
      <span className="ledger__delta" data-sign={line.delta >= 0 ? 'up' : 'down'}>
        {signedChips(line.delta)}
      </span>
      <span className="ledger__after">{chips(line.balanceAfter)}</span>
    </div>
  );
}

/** Ledger kinds in the words a player would use, not the words the DB uses. */
const KIND_LABEL: Record<LedgerKind, CopyKey> = {
  starter_rack: 'account.kind.starter_rack',
  daily_baon: 'account.kind.daily_baon',
  hourly_rescue: 'account.kind.hourly_rescue',
  iap_topup: 'account.kind.iap_topup',
  buyin_escrow: 'account.kind.buyin_escrow',
  buyin_refund: 'account.kind.buyin_refund',
  pot_payout: 'account.kind.pot_payout',
  tong: 'account.kind.tong',
  admin_adjust: 'account.kind.admin_adjust',
};
